import React, { useEffect, useState } from 'react';
import moment from 'moment';
import RectangleTree from '../RectangleTree';
import WorldMap from '@intelligence/pages/MarketProfile/components/WorldMap';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import LineChart from '@/pages/intelligence/components/Chart/components/LineChart';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import { ITableData, IGenreType } from '../../typings';
import { valueFormat } from '@/pages/intelligence/utils/commonUtils';
import { formatPercentNum } from '../../utils';
import ChartBubble, { TOP_CHARTS_TYPES } from '@/pages/intelligence/components/ChartBubble';

type TVisualisType = 'chart-treemap' | 'chart-line' | 'world-map' | 'discount-chart';

interface IVisualisProps {
  isLoading: boolean;
  visualisType: TVisualisType;
  filter?: any;
  tableAction?: any;
  lineChartData?: any;
  entityType?: string;
  metric?: string;
  echartsMetricsCur?: string;
  pageId?: string;
  mapData?: any[];
  piecesMap?: any;
  nameMap?: any;
  headerLeftTitle?: string;
  leftContent?: string;
}

/**
 * A visualization component that renders different types of charts based on the provided data and configuration.
 * @param props - The component props
 * @param props.isLoading - Loading state of the component
 * @param props.filter - Filter configuration for data
 * @param props.tableAction - Actions and data for table operations
 * @param props.lineChartData - Data for line chart visualization
 * @param props.entityType - Type of entity, defaults to 'mobile'
 * @param props.visualisType - Type of visualization ('chart-treemap', 'chart-line', 'world-map')
 * @param props.metric - Metric to display in charts
 * @param props.echartsMetricsCur - Current metrics for echarts
 * @param props.pageId - Page identifier, defaults to 'example'
 * @param props.mapData - Data for world map visualization
 * @param props.piecesMap - Map pieces configuration
 * @param props.nameMap - Name mapping configuration
 * @param props.headerLeftTitle - Left header title
 * @param props.leftContent - Left content section
 * @returns A React component that renders the appropriate visualization based on props
 */
export default function Visualis(props: IVisualisProps) {
  const {
    isLoading,
    filter,
    tableAction,
    lineChartData,
    entityType = 'mobile',
    visualisType,
    metric,
    echartsMetricsCur,
    pageId = 'example',
    mapData = [],
    piecesMap,
    nameMap,
    headerLeftTitle,
    leftContent,
  } = props;

  const getAddedParams = (chartItem: ITableData, locale) => {
    const { mainGenre, mainGenreCn, subGenre, subGenreCn, metric_list } = chartItem;
    const find = metric_list?.find((item) => item.metric === metric);
    const total = tableAction.selectedTotalValue[metric];
    const isCn = locale === 'zh-CN';
    const { value = 0 } = find || {};
    const getName = (type: IGenreType) => {
      switch (type) {
        case 'main':
          return isCn ? mainGenreCn : mainGenre;
        case 'sub':
          return isCn ? subGenreCn : subGenre;
        default:
          return isCn ? `${mainGenreCn}-${subGenreCn}` : `${mainGenre}-${subGenre}`;
      }
    };
    return {
      name: getName(filter.genreType),
      value,
      formattedValue: valueFormat(value),
      percent: formatPercentNum(value / total),
      time: `${moment(filter.startDate).format('MMM, YYYY')} ~ ${moment(filter.endDate).format(
        'MMM, YYYY',
      )}`,
      isShow: true,
    };
  };

  const getRenderContent = (chartItem: any) => {
    const { name, formattedValue, percent } = chartItem;
    const html =
      '{name|' +
      `${name}` +
      '}\n' +
      `{num|${metric === 'downloads' ? '' : '$'}` +
      `${formattedValue}` +
      '}\n' +
      '{precent|' +
      `${percent}` +
      '}';
    return html;
  };

  const getTooltip = (chartItem: any) => {
    // console.log('chartItem', chartItem);
    const { name, formattedValue, percent, time } = chartItem;
    return ChartBubble({
      topChartsType: TOP_CHARTS_TYPES.GENRE_PROFILE_TOP_TREE,
      chartType: 'tree',
      secondTitleLeft: time,
      // data: [name + ' ' + formattedValue + ' ' + percent],
      otherTitleLeft: name,
      otherTitleRight: `${formattedValue}  ${percent}`,
      // secondTitleRight: echartsMetricsCur
    });
  };

  // 获取折线图hooks
  const { lineOption } = useLineCharts({
    lineData: lineChartData, // 折线 数据
    dateType: 'Month',
    imageType: entityType,
    metric,
    topChartsType: 'genreProfile-index',
    secondTitleRight: echartsMetricsCur,
  });
  /**
   * 根据类型返回不同的可视化图表组件
   * @param type - 可视化类型，支持 'chart-treemap' (矩形树图) 或 'chart-line' (折线图)
   * @returns 返回对应的图表组件，如果是树图且没有选中数据则返回 null
   * - chart-treemap: 返回矩形树组件，展示选中数据的层级结构
   * - chart-line: 返回折线图组件，展示数据趋势
   */
  const getVisualis = (type: TVisualisType) => {
    switch (type) {
      case 'chart-treemap':
        if (tableAction.selectedTotalValue) {
          return (
            <RectangleTree
              uniqueKey={entityType}
              data={tableAction.selectedList}
              metric={metric}
              getAddedParams={getAddedParams}
              getRenderContent={getRenderContent}
              getTooltip={getTooltip}
            />
          );
        }
        return null;
      case 'discount-chart':
        return <LineChart height={300} lineOption={lineOption} />;
      case 'chart-line':
        return <LineChart height={300} lineOption={lineOption} />;
      case 'world-map':
        return (
          <WorldMap
            pageId={pageId}
            mapData={mapData}
            piecesMap={piecesMap}
            nameMap={nameMap}
            headerLeftTitle={headerLeftTitle}
            leftContent={leftContent}
          />
        );
      default:
        return null;
    }
  };

  /**
   * 判断图表是否无数据
   * @param type - 图表类型,'chart-treemap'为树图,'chart-line'为折线图
   * @returns 当不在加载状态且满足对应图表类型的条件时返回true,表示无数据
   */
  const getIsNoData = (type: TVisualisType) => {
    let anotherCond = true;
    if (type === 'chart-treemap') {
      anotherCond = !tableAction.selectedList?.length;
    } else if (type === 'chart-line') {
      anotherCond = !lineChartData?.[metric]?.length;
    } else if (type === 'discount-chart') {
      anotherCond = !lineChartData?.[metric]?.length;
    }
    return !isLoading && anotherCond;
  };

  return (
    <div className="chart-data">
      <Abnormal
        isLoading={isLoading}
        isNoData={getIsNoData(visualisType)}
        component={getVisualis(visualisType)}
      />
    </div>
  );
}
